import * as React from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from '../../config/apiConfig';

function Order() {
  const location = useLocation();
  const navigate = useNavigate();
  const [orderStatus, setOrderStatus] = React.useState('loading');
  const [orderData, setOrderData] = React.useState(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    // Get order_id from session storage
    const sessionOrderId = sessionStorage.getItem('current_order_id');
    const orderType = sessionStorage.getItem('order_type') || 'subscription'; // default to subscription for backward compatibility
    
    // Also check URL parameters as fallback
    const queryParams = new URLSearchParams(location.search);
    const urlOrderId = queryParams.get('order_id');
    const status = queryParams.get('status');
    const mandateStatus = queryParams.get('mandate_status');
    const mandateId = queryParams.get('mandate_id');
    const statusId = queryParams.get('status_id');
    const transactionId = queryParams.get('transaction_id');
    const error = queryParams.get('error');
    
    // Prefer session storage order_id, fallback to URL
    const orderId = sessionOrderId || urlOrderId;
    
    console.log('Order page - Session Order ID:', sessionOrderId);
    console.log('Order page - Order Type:', orderType);
    console.log('Order page - URL params:', { 
      urlOrderId, 
      status, 
      mandateStatus, 
      mandateId, 
      statusId, 
      transactionId,
      error 
    });
    console.log('Order page - Using Order ID:', orderId);

    // If we have payment callback data from URL (direct from payment gateway)
    if (status && urlOrderId) {
      console.log('Processing payment callback data from URL');
      setOrderStatus(status.toLowerCase());
      setOrderData({
        order_id: urlOrderId,
        status: status,
        mandate_status: mandateStatus,
        mandate_id: mandateId,
        status_id: statusId,
        transaction_id: transactionId,
        order_type: orderType,
        message: error || getPaymentMessage(status, mandateStatus, orderType)
      });
      return;
    }

    if (orderId) {
      // If we have an order ID, fetch the order status based on order type
      fetchOrderStatus(orderId, orderType);
    } else {
      // If no order ID and no URL callback data
      if (error) {
        setOrderStatus('error');
        setError(error);
      } else {
        setOrderStatus('error');
        setError('No order information found. Please try initiating the payment again.');
      }
    }
  }, [location]);

  const fetchOrderStatus = async (orderId, orderType = 'subscription') => {
    try {
      console.log('Fetching order status for order_id:', orderId, 'type:', orderType);
      const token = sessionStorage.getItem('token');
      
      // Choose the correct API endpoint based on order type
      let apiUrl;
      if (orderType === 'product') {
        apiUrl = `${API_BASE_URL}/product/order/${orderId}`;
      } else {
        apiUrl = `${API_BASE_URL}/subscription/mandate-status/${orderId}`;
      }
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token
        }
      });

      const result = await response.json();
      console.log('Order status API response:', result);
      
      if (result.is_success) {
        // Add order type to the data for UI display purposes
        const orderDataWithType = {
          ...result.data,
          order_type: orderType
        };
        setOrderData(orderDataWithType);
        const newStatus = result.data.status ? result.data.status.toLowerCase() : 'pending';
        console.log('Setting order status to:', newStatus);
        setOrderStatus(newStatus);
      } else {
        console.error('Order status API error:', result);
        setOrderStatus('error');
        setError(result.message || 'Failed to fetch order status');
      }
    } catch (error) {
      console.error('Error fetching order status:', error);
      setOrderStatus('error');
      setError('Error fetching order status. Please try again.');
    }
  };

  const fetchOrderStatusFromGateway = async (orderId, orderType = 'subscription') => {
    try {
      console.log('Fetching order status from gateway for order_id:', orderId, 'type:', orderType);
      const token = sessionStorage.getItem('token');
      
      // Choose the correct gateway API endpoint based on order type
      let apiUrl;
      if (orderType === 'product') {
        apiUrl = `${API_BASE_URL}/product/order/${orderId}/gateway-status`;
      } else {
        // For subscriptions, fall back to the regular status check
        apiUrl = `${API_BASE_URL}/subscription/mandate-status/${orderId}`;
      }
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token
        }
      });

      const result = await response.json();
      console.log('Gateway status API response:', result);
      
      if (result.is_success) {
        // Add order type to the data for UI display purposes
        const orderDataWithType = {
          ...result.data,
          order_type: orderType
        };
        setOrderData(orderDataWithType);
        const newStatus = result.data.status ? result.data.status.toLowerCase() : 'pending';
        console.log('Setting order status from gateway to:', newStatus);
        setOrderStatus(newStatus);
      } else {
        console.error('Gateway status API error:', result);
        setOrderStatus('error');
        setError(result.message || 'Failed to fetch order status from gateway');
      }
    } catch (error) {
      console.error('Error fetching order status from gateway:', error);
      
      // If gateway check fails, fall back to local status check
      console.log('Gateway check failed, falling back to local status check');
      fetchOrderStatus(orderId, orderType);
    }
  };

  const handleGoHome = () => {
    // Clear order info from session storage when navigating away
    sessionStorage.removeItem('current_order_id');
    sessionStorage.removeItem('order_type');
    navigate('/profile');
  };

  const handleGoToProfile = () => {
    // Clear order info from session storage when navigating away
    sessionStorage.removeItem('current_order_id');
    sessionStorage.removeItem('order_type');
    
    // Navigate to profile with appropriate section parameter for successful payments
    if (orderStatus === 'success' || orderStatus === 'completed' || orderStatus === 'active') {
      const orderType = orderData?.order_type || 'subscription';
      if (orderType === 'product') {
        navigate('/profile?section=orders');
      } else {
        navigate('/profile?section=subscription');
      }
    } else {
      navigate('/profile');
    }
  };

  // Clear order info from session storage when payment is successful
  React.useEffect(() => {
    if (orderStatus === 'success' || orderStatus === 'completed' || orderStatus === 'active') {
      // Clear the order info from session storage after successful payment
      setTimeout(() => {
        sessionStorage.removeItem('current_order_id');
        sessionStorage.removeItem('order_type');
        console.log('Cleared order info from sessionStorage after successful payment');
      }, 5000); // Clear after 5 seconds to allow user to see the success message
    }
  }, [orderStatus]);

  // Cleanup on component unmount
  React.useEffect(() => {
    return () => {
      // Only clear if the payment was successful, otherwise keep it for retry
      if (orderStatus === 'success' || orderStatus === 'completed' || orderStatus === 'active') {
        sessionStorage.removeItem('current_order_id');
        sessionStorage.removeItem('order_type');
      }
    };
  }, [orderStatus]);

  const handleRefreshStatus = () => {
    const sessionOrderId = sessionStorage.getItem('current_order_id');
    const orderType = sessionStorage.getItem('order_type') || 'subscription';
    if (sessionOrderId) {
      setOrderStatus('loading');
      setError('');
      // Check status from gateway for real-time updates
      fetchOrderStatusFromGateway(sessionOrderId, orderType);
    } else {
      setError('No order information found in session. Please try initiating the payment again.');
      setOrderStatus('error');
    }
  };

  // Helper function to get payment message based on status
  const getPaymentMessage = (status, mandateStatus, orderType = 'subscription') => {
    if (status === 'success' || status === 'completed') {
      if (orderType === 'product') {
        return 'Payment successful! Your order has been confirmed and will be processed.';
      } else {
        return mandateStatus === 'active' 
          ? 'Payment successful! Your subscription is now active.'
          : 'Payment completed successfully.';
      }
    } else if (status === 'failed') {
      if (orderType === 'product') {
        return 'Order payment failed. Please try again or contact support.';
      } else {
        return 'Payment failed. Please try again or contact support.';
      }
    } else if (status === 'cancelled') {
      if (orderType === 'product') {
        return 'Order was cancelled. You can retry if needed.';
      } else {
        return 'Payment was cancelled. You can retry if needed.';
      }
    }
    return orderType === 'product' ? 'Order status updated.' : 'Payment status updated.';
  };

  // Render product order success view
  const renderProductOrderSuccess = () => (
    <StatusContainer>
      <StatusTitle>Order Confirmed!</StatusTitle>
      <StatusMessage>
        Your order has been confirmed and will be processed shortly. You'll receive updates via email/ SMS.
      </StatusMessage>
      
      {orderData && orderData.product && (
        <ProductOrderCard>
          <ProductImage 
            src={orderData.product.images && orderData.product.images[0] 
              ? orderData.product.images[0] 
              : '/default-product.png'
            } 
            alt={orderData.product.name}
            onError={(e) => {
              e.target.src = '/default-product.png';
            }}
          />
          <ProductInfo>
            <ProductName>{orderData.product.name}</ProductName>
            <ProductPrice>₹{parseFloat(orderData.amount).toLocaleString('en-IN')}</ProductPrice>
            {orderData.product.brand && <ProductBrand>{orderData.product.brand}</ProductBrand>}
          </ProductInfo>
        </ProductOrderCard>
      )}

      {orderData && (
        <OrderDetails>
          <DetailRow>
            <DetailLabel>Order ID:</DetailLabel>
            <DetailValue>{orderData.order_id}</DetailValue>
          </DetailRow>
          {orderData.order_status && (
            <DetailRow>
              <DetailLabel>Order Status:</DetailLabel>
              <DetailValue>{orderData.order_status.replace(/\b\w/g, c => c.toUpperCase())}</DetailValue>
            </DetailRow>
          )}
          {orderData.payment_details && orderData.payment_details.txn_id && (
            <DetailRow>
              <DetailLabel>Transaction ID:</DetailLabel>
              <DetailValue>{orderData.payment_details.txn_id}</DetailValue>
            </DetailRow>
          )}
          {orderData.customer_info && (
            <DetailRow>
              <DetailLabel>Delivery to:</DetailLabel>
              <DetailValue>{orderData.customer_info.name}</DetailValue>
            </DetailRow>
          )}
          {orderData.local_order && orderData.local_order.shipping_address && (
            <DetailRow>
              <DetailLabel>Address:</DetailLabel>
              <DetailValue>
                {orderData.local_order.shipping_address.address}, {orderData.local_order.shipping_address.city}, {orderData.local_order.shipping_address.state} - {orderData.local_order.shipping_address.pincode}
              </DetailValue>
            </DetailRow>
          )}
          <DetailRow>
            <DetailLabel>Payment Status:</DetailLabel>
            <DetailValue style={{ color: '#28a745', fontWeight: 'bold' }}>
              {orderData.status?.toUpperCase()}
            </DetailValue>
          </DetailRow>
          {orderData.payment_details && orderData.payment_details.payment_method && (
            <DetailRow>
              <DetailLabel>Payment Method:</DetailLabel>
              <DetailValue>{orderData.payment_details.payment_method}</DetailValue>
            </DetailRow>
          )}
        </OrderDetails>
      )}

      <NextStepsSection>
        <NextStepsTitle>What's Next?</NextStepsTitle>
        <NextStepsList>
          <NextStepItem>You'll receive order confirmation via email</NextStepItem>
          <NextStepItem>Order will be processed within 1-2 business days</NextStepItem>
          <NextStepItem>Shipping updates will be sent to your registered mobile number</NextStepItem>
        </NextStepsList>
      </NextStepsSection>

      <ButtonContainer>
        <ActionButton primary onClick={handleGoToProfile}>
          View My Orders
        </ActionButton>
        <ActionButton onClick={handleGoHome}>
          Continue Shopping
        </ActionButton>
      </ButtonContainer>
    </StatusContainer>
  );

  // Render subscription success view
  const renderSubscriptionSuccess = () => (
    <StatusContainer>
      <StatusTitle>Subscription Activated!</StatusTitle>
      <StatusMessage>
        Your subscription mandate has been activated successfully. You'll now receive exclusive deals and benefits.
      </StatusMessage>
      
      {orderData && (
        <OrderDetails>
          <DetailRow>
            <DetailLabel>Order ID:</DetailLabel>
            <DetailValue>{orderData.order_id}</DetailValue>
          </DetailRow>
          {orderData.transaction_id && (
            <DetailRow>
              <DetailLabel>Transaction ID:</DetailLabel>
              <DetailValue>{orderData.transaction_id}</DetailValue>
            </DetailRow>
          )}
          {orderData.payment_method && (
            <DetailRow>
              <DetailLabel>Payment Method:</DetailLabel>
              <DetailValue>{orderData.payment_method}</DetailValue>
            </DetailRow>
          )}
          {orderData.mandate_id && (
            <DetailRow>
              <DetailLabel>Mandate ID:</DetailLabel>
              <DetailValue>{orderData.mandate_id}</DetailValue>
            </DetailRow>
          )}
          {orderData.mandate_status && (
            <DetailRow>
              <DetailLabel>Mandate Status:</DetailLabel>
              <DetailValue style={{ color: '#28a745', fontWeight: 'bold' }}>
                {orderData.mandate_status.toUpperCase()}
              </DetailValue>
            </DetailRow>
          )}
          <DetailRow>
            <DetailLabel>Status:</DetailLabel>
            <DetailValue style={{ color: '#28a745', fontWeight: 'bold' }}>
              {orderData.status?.toUpperCase()}
            </DetailValue>
          </DetailRow>
          {orderData.amount && (
            <DetailRow>
              <DetailLabel>Amount:</DetailLabel>
              <DetailValue>₹{parseFloat(orderData.amount).toLocaleString('en-IN')}</DetailValue>
            </DetailRow>
          )}
        </OrderDetails>
      )}

      <NextStepsSection>
        <NextStepsTitle>Subscription Benefits</NextStepsTitle>
        <NextStepsList>
          <NextStepItem>Access to exclusive member-only deals</NextStepItem>
          <NextStepItem>Priority notifications for flash sales</NextStepItem>
          <NextStepItem>Automatic deduction for hassle-free shopping</NextStepItem>
        </NextStepsList>
      </NextStepsSection>

      <ButtonContainer>
        <ActionButton primary onClick={handleGoToProfile}>
          View Subscription Details
        </ActionButton>
        <ActionButton onClick={handleGoHome}>
          Start Shopping
        </ActionButton>
      </ButtonContainer>
    </StatusContainer>
  );

  const renderOrderStatus = () => {
    switch (orderStatus) {
      case 'loading':
        return (
          <StatusContainer>
            <StatusTitle>Processing...</StatusTitle>
            <StatusMessage>Please wait while we verify your payment</StatusMessage>
          </StatusContainer>
        );
      
      case 'success':
      case 'completed':
      case 'active':
        const isProductOrder = orderData?.order_type === 'product';
        
        if (isProductOrder) {
          return renderProductOrderSuccess();
        } else {
          return renderSubscriptionSuccess();
        }
      
      case 'failed':
      case 'cancelled':
      case 'error':
        const isFailedProductOrder = orderData?.order_type === 'product';
        return (
          <StatusContainer>
            <StatusTitle>{isFailedProductOrder ? 'Order Failed' : 'Payment Failed'}</StatusTitle>
            <StatusMessage>
              {error || (isFailedProductOrder 
                ? 'Your order could not be processed. Please try again.' 
                : 'Your payment could not be processed. Please try again.'
              )}
            </StatusMessage>
            {orderData && orderData.order_id && (
              <OrderDetails>
                <DetailRow>
                  <DetailLabel>Order ID:</DetailLabel>
                  <DetailValue>{orderData.order_id}</DetailValue>
                </DetailRow>
                {orderData.status && (
                  <DetailRow>
                    <DetailLabel>Status:</DetailLabel>
                    <DetailValue style={{ color: '#dc3545', fontWeight: 'bold' }}>
                      {orderData.status?.toUpperCase()}
                    </DetailValue>
                  </DetailRow>
                )}
              </OrderDetails>
            )}
            <ButtonContainer>
              <ActionButton primary onClick={handleGoToProfile}>
                {isFailedProductOrder ? 'Try Ordering Again' : 'Try Payment Again'}
              </ActionButton>
              <ActionButton onClick={handleGoHome}>
                Go to Home
              </ActionButton>
            </ButtonContainer>
          </StatusContainer>
        );
      
      case 'pending':
      default:
        const isPendingProductOrder = orderData?.order_type === 'product';
        return (
          <StatusContainer>
            <StatusTitle>{isPendingProductOrder ? 'Order Processing' : 'Payment Pending'}</StatusTitle>
            <StatusMessage>
              {isPendingProductOrder 
                ? 'Your order is being processed. Please wait for confirmation.'
                : 'Your payment is being processed. Please wait for confirmation.'
              }
            </StatusMessage>
            {orderData && (
              <OrderDetails>
                <DetailRow>
                  <DetailLabel>Order ID:</DetailLabel>
                  <DetailValue>{orderData.order_id}</DetailValue>
                </DetailRow>
                {orderData.order_status && (
                  <DetailRow>
                    <DetailLabel>Order Status:</DetailLabel>
                    <DetailValue>{orderData.order_status.replace(/\b\w/g, c => c.toUpperCase())}</DetailValue>
                  </DetailRow>
                )}
                <DetailRow>
                  <DetailLabel>Status:</DetailLabel>
                  <DetailValue>{orderData.status?.toUpperCase()}</DetailValue>
                </DetailRow>
                {orderData.last_updated && (
                  <DetailRow>
                    <DetailLabel>Last Updated:</DetailLabel>
                    <DetailValue>{new Date(orderData.last_updated).toLocaleString()}</DetailValue>
                  </DetailRow>
                )}
              </OrderDetails>
            )}
            <ButtonContainer>
              <ActionButton primary onClick={handleRefreshStatus}>
                {isPendingProductOrder ? 'Check Latest Status' : 'Refresh Status'}
              </ActionButton>
              <ActionButton onClick={handleGoHome}>
                Go to Home
              </ActionButton>
            </ButtonContainer>
          </StatusContainer>
        );
    }
  };

  return (
    <OrderContainer>
      <ContentWrapper>
        <Header>
          <Title>Order Status</Title>
        </Header>
        {renderOrderStatus()}
      </ContentWrapper>
    </OrderContainer>
  );
}

// Styled Components
const OrderContainer = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ContentWrapper = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 40px;
  max-width: 600px;
  width: 100%;
  text-align: center;
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font: 800 24px Inter, sans-serif;
  color: #202023;
  margin: 0;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StatusIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
  ${props => props.success && `color: #4caf50;`}
  ${props => props.error && `color: #f44336;`}
`;

const StatusTitle = styled.h2`
  font: 800 20px Inter, sans-serif;
  color: #202023;
  margin: 0 0 10px 0;
`;

const StatusMessage = styled.p`
  font: 400 16px Metrophobic, sans-serif;
  color: #666;
  margin: 0 0 30px 0;
  line-height: 1.5;
`;

const OrderDetails = styled.div`
  background-color: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 20px;
  margin: 20px 0;
  text-align: left;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font: 600 14px Inter, sans-serif;
  color: #666;
`;

const DetailValue = styled.span`
  font: 400 14px Metrophobic, sans-serif;
  color: #202023;
  word-break: break-all;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 30px;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  padding: 12px 24px;
  font: 600 14px Inter, sans-serif;
  text-transform: uppercase;
  letter-spacing: -0.24px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.primary ? `
    background-color: #e9fe53;
    border: 1px solid #202023;
    color: #202023;
    
    &:hover {
      background-color: #d4e947;
    }
  ` : `
    background-color: transparent;
    border: 1px solid #202023;
    color: #202023;
    
    &:hover {
      background-color: #f5f5f5;
    }
  `}
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

// Product Order Specific Styled Components
const ProductOrderCard = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  background-color: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
`;

const ProductImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const ProductName = styled.h3`
  font: 600 16px Inter, sans-serif;
  color: #202023;
  margin: 0 0 5px 0;
  line-height: 1.3;
`;

const ProductPrice = styled.p`
  font: 700 18px Inter, sans-serif;
  color: #202023;
  margin: 5px 0;
`;

const ProductBrand = styled.p`
  font: 400 14px Metrophobic, sans-serif;
  color: #666;
  margin: 0;
`;

const NextStepsSection = styled.div`
  background-color: #f0f8ff;
  border: 1px solid #e9fe53;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
`;

const NextStepsTitle = styled.h3`
  font: 600 16px Inter, sans-serif;
  color: #202023;
  margin: 0 0 15px 0;
`;

const NextStepsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NextStepItem = styled.li`
  font: 400 14px Metrophobic, sans-serif;
  color: #333;
  margin-bottom: 8px;
  padding-left: 0;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export default Order;